<template>
  <b-modal
    id="product-select-modal"
    :visible="isVisible"
    size="lg"
    :title="$t('Select products')"
    hide-footer
    centered
    scrollable
    modal-class="product-select-modal"
    @hidden="$emit('update:is-visible', false)"
  >
    <!-- Search & Filter Header -->
    <div class="product-select-header mb-2">
      <div class="d-flex align-items-center gap-2">
        <b-input-group class="flex-grow-1">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('Search products')"
            class="search-input"
          />
        </b-input-group>
        <v-select
          v-model="searchFilter"
          :options="filterOptions"
          :reduce="opt => opt.value"
          label="label"
          :clearable="false"
          class="search-filter-select"
          :placeholder="$t('Search by')"
        />
      </div>
    </div>

    <!-- Products Table Header -->
    <div class="products-table-header">
      <div class="col-checkbox" />
      <div class="col-product">{{ $t('Product') }}</div>
      <div class="col-available">{{ $t('Available') }}</div>
      <div class="col-price">{{ $t('Price') }}</div>
    </div>

    <!-- Products List -->
    <div class="products-list">
      <div
        v-if="isLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <template v-else-if="filteredProducts.length">
        <template v-for="group in filteredProducts">
          <!-- Product Group with multiple products (expandable) -->
          <div
            v-if="group.products && group.products.length > 1"
            :key="`group-${group.id}`"
            class="product-group"
          >
            <!-- Group Header Row -->
            <div
              class="product-row product-parent"
              @click="toggleProductExpand(group)"
            >
              <div class="col-checkbox">
                <b-form-checkbox
                  :checked="isAllGroupProductsSelected(group)"
                  :indeterminate="isSomeGroupProductsSelected(group)"
                  @change="toggleAllGroupProducts(group, $event)"
                  @click.native.stop
                />
              </div>
              <div class="col-product d-flex align-items-center">
                <feather-icon
                  :icon="expandedProducts.includes(group.id) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                  class="mr-50 expand-icon"
                />
                <b-img
                  :src="group.image ? storagePath(group.image) : ImagePlaceholder"
                  :alt="group.name"
                  class="product-image mr-1"
                />
                <span class="product-name">{{ group.name }}</span>
              </div>
              <div class="col-available" />
              <div class="col-price" />
            </div>

            <!-- Sub-Product Rows -->
            <template v-if="expandedProducts.includes(group.id)">
              <div
                v-for="subProduct in group.products"
                :key="`sub-${subProduct.id}`"
                class="product-row product-variant"
                :class="{ 'product-disabled': isProductAlreadyInOrder(subProduct) }"
                @click="!isProductAlreadyInOrder(subProduct) && toggleProductSelection(subProduct, group)"
              >
                <div class="col-checkbox">
                  <b-form-checkbox
                    :checked="isProductAlreadyInOrder(subProduct) || isProductSelected(subProduct)"
                    :disabled="isProductAlreadyInOrder(subProduct)"
                    @change="toggleProductSelection(subProduct, group)"
                    @click.native.stop
                  />
                </div>
                <div class="col-product variant-name d-flex align-items-center">
                  {{ getSubProductDisplayName(subProduct, group) }}
                  <b-badge
                    v-if="isProductAlreadyInOrder(subProduct)"
                    variant="light-secondary"
                    class="ml-50"
                  >
                    {{ $t('Added') }}
                  </b-badge>
                </div>
                <div class="col-available">
                  {{ subProduct.has_unlimited_qty ? '∞' : (subProduct.qty || '-') }}
                </div>
                <div class="col-price">
                  {{ formatPrice(subProduct.price) }}
                </div>
              </div>
            </template>
          </div>

          <!-- Single Product (group with one item) -->
          <div
            v-else-if="group.products && group.products.length === 1"
            :key="`single-${group.id}`"
            class="product-row"
            :class="{ 'product-disabled': isProductAlreadyInOrder(group.products[0]) }"
            @click="!isProductAlreadyInOrder(group.products[0]) && toggleProductSelection(group.products[0], group)"
          >
            <div class="col-checkbox">
              <b-form-checkbox
                :checked="isProductAlreadyInOrder(group.products[0]) || isProductSelected(group.products[0])"
                :disabled="isProductAlreadyInOrder(group.products[0])"
                @change="toggleProductSelection(group.products[0], group)"
                @click.native.stop
              />
            </div>
            <div class="col-product d-flex align-items-center">
              <b-img
                :src="group.image ? storagePath(group.image) : ImagePlaceholder"
                :alt="group.name"
                class="product-image mr-1"
              />
              <span class="product-name">{{ group.name }}</span>
              <b-badge
                v-if="isProductAlreadyInOrder(group.products[0])"
                variant="light-secondary"
                class="ml-50"
              >
                {{ $t('Added') }}
              </b-badge>
            </div>
            <div class="col-available">
              {{ group.products[0].has_unlimited_qty ? '∞' : (group.products[0].qty || '-') }}
            </div>
            <div class="col-price">
              {{ formatPrice(group.products[0].price) }}
            </div>
          </div>
        </template>
      </template>

      <div
        v-else
        class="text-center py-3 text-muted"
      >
        {{ $t('No products found') }}
      </div>
    </div>

    <!-- Footer -->
    <div class="product-select-footer">
      <div class="selected-count">
        {{ selectedProducts.length }} {{ $t('products selected') }}
      </div>
      <div class="footer-actions">
        <b-button
          variant="outline-secondary"
          @click="$emit('update:is-visible', false)"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedProducts.length"
          @click="addSelectedProducts"
        >
          {{ $t('Add') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BFormInput,
  BFormCheckbox,
  BButton,
  BImg,
  BSpinner,
  BInputGroup,
  BInputGroupPrepend,
  BBadge,
} from 'bootstrap-vue'
import { ref, computed, watch, getCurrentInstance } from 'vue'
import vSelect from 'vue-select'
import { storagePath } from '@core/utils/utils'

export default {
  components: {
    BModal,
    BFormInput,
    BFormCheckbox,
    BButton,
    BImg,
    BSpinner,
    BInputGroup,
    BInputGroupPrepend,
    BBadge,
    vSelect,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
    existingProducts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:is-visible', 'products-selected'],
  methods: { storagePath },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const $t = key => instance.proxy.$t(key)

    const searchQuery = ref('')
    const searchFilter = ref('all')
    const selectedProducts = ref([])
    const expandedProducts = ref([])
    const isLoading = ref(false)

    const ImagePlaceholder = require('@/assets/images/pages/eCommerce/placeholder.webp')

    const filterOptions = computed(() => [
      { label: $t('All'), value: 'all' },
      { label: $t('In Stock'), value: 'in_stock' },
      { label: $t('Out of Stock'), value: 'out_of_stock' },
    ])

    // Products are already grouped from API - use directly
    const filteredProducts = computed(() => {
      let result = props.products || []

      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(group => {
          // Search in group name
          if (group.name?.toLowerCase().includes(query)) return true
          // Search in sub-products
          if (group.products?.some(p => p.name?.toLowerCase().includes(query))) return true
          return false
        })
      }

      // Apply stock filter
      if (searchFilter.value === 'in_stock') {
        result = result.filter(group => {
          if (group.products?.length) {
            return group.products.some(p => p.has_unlimited_qty || p.qty > 0)
          }
          return false
        })
      } else if (searchFilter.value === 'out_of_stock') {
        result = result.filter(group => {
          if (group.products?.length) {
            return group.products.every(p => !p.has_unlimited_qty && (!p.qty || p.qty <= 0))
          }
          return false
        })
      }

      return result
    })

    const formatPrice = price => {
      if (!price) return '-'
      // Handle price object from API { amount, currency, string }
      if (typeof price === 'object') {
        // Use the pre-formatted string if available
        if (price.string) return price.string
        const amount = price.amount / 100
        const currency = price.currency || 'USD'
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        }).format(amount)
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(price)
    }

    const isProductSelected = product => {
      const productId = product.id || product.product_id
      return selectedProducts.value.some(p => (p.id || p.product_id) === productId)
    }

    const isProductAlreadyInOrder = product => {
      const productId = product.id || product.product_id
      return props.existingProducts.some(p => (p.id || p.product_id) === productId)
    }

    const toggleProductSelection = (product, group = null) => {
      const productId = product.id || product.product_id
      const index = selectedProducts.value.findIndex(p => (p.id || p.product_id) === productId)
      if (index >= 0) {
        selectedProducts.value.splice(index, 1)
      } else {
        // Add product with group info for display purposes
        selectedProducts.value.push({
          ...product,
          group_id: group?.id,
          group_name: group?.name,
          group_image: group?.image,
        })
      }
    }

    const toggleProductExpand = group => {
      const index = expandedProducts.value.indexOf(group.id)
      if (index >= 0) {
        expandedProducts.value.splice(index, 1)
      } else {
        expandedProducts.value.push(group.id)
      }
    }

    const isAllGroupProductsSelected = group => {
      if (!group.products?.length) return false
      return group.products.every(p => isProductSelected(p))
    }

    const isSomeGroupProductsSelected = group => {
      if (!group.products?.length) return false
      const selectedCount = group.products.filter(p => isProductSelected(p)).length
      return selectedCount > 0 && selectedCount < group.products.length
    }

    const toggleAllGroupProducts = (group, checked) => {
      if (checked) {
        group.products.forEach(product => {
          if (!isProductSelected(product)) {
            selectedProducts.value.push({
              ...product,
              group_id: group.id,
              group_name: group.name,
              group_image: group.image,
            })
          }
        })
      } else {
        group.products.forEach(product => {
          const index = selectedProducts.value.findIndex(p => (p.id || p.product_id) === product.id)
          if (index >= 0) {
            selectedProducts.value.splice(index, 1)
          }
        })
      }
    }

    // Get display name for sub-product (show variant name if different from group)
    const getSubProductDisplayName = (subProduct, group) => {
      // If names are identical, just show the name
      if (subProduct.name === group.name) {
        return subProduct.name
      }
      // Try to extract variant part (e.g., "Product (Red)" -> "Red")
      const variantMatch = subProduct.name.match(/\(([^)]+)\)$/)
      if (variantMatch) {
        return variantMatch[1]
      }
      // Otherwise show the full sub-product name
      return subProduct.name
    }

    const addSelectedProducts = () => {
      emit('products-selected', selectedProducts.value)
      emit('update:is-visible', false)
      selectedProducts.value = []
    }

    // Reset selection and expand all groups when modal opens
    watch(() => props.isVisible, visible => {
      if (visible) {
        selectedProducts.value = []
        searchQuery.value = ''
        // Expand all product groups by default
        expandedProducts.value = props.products
          .filter(group => group.products && group.products.length > 1)
          .map(group => group.id)
      }
    })

    return {
      searchQuery,
      searchFilter,
      filterOptions,
      selectedProducts,
      expandedProducts,
      isLoading,
      filteredProducts,
      ImagePlaceholder,

      formatPrice,
      isProductSelected,
      isProductAlreadyInOrder,
      toggleProductSelection,
      toggleProductExpand,
      isAllGroupProductsSelected,
      isSomeGroupProductsSelected,
      toggleAllGroupProducts,
      getSubProductDisplayName,
      addSelectedProducts,
    }
  },
}
</script>

<style lang="scss">
.product-select-modal {
  .modal-body {
    padding: 0;
  }

  .product-select-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
    background: #fafbfc;

    .search-input {
      border-radius: 8px;
    }

    .search-filter-select {
      min-width: 150px;

      .vs__dropdown-toggle {
        border-radius: 8px;
        border-color: #d8d6de;
      }
    }
  }

  .products-table-header {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    font-weight: 600;
    font-size: 0.85rem;
    color: #6c757d;
  }

  .products-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .product-row {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f8f9fa;
    }

    &.product-variant {
      padding-left: 3.5rem;
      background-color: #fafbfc;

      .variant-name {
        padding-left: 2.5rem;
        color: #6c757d;
      }
    }

    &.product-parent {
      font-weight: 500;
    }

    &.product-disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background-color: #f8f9fa;

      &:hover {
        background-color: #f8f9fa;
      }

      .product-name,
      .variant-name {
        color: #adb5bd;
      }
    }
  }

  .col-checkbox {
    width: 40px;
    flex-shrink: 0;
  }

  .col-product {
    flex: 1;
    min-width: 0;
  }

  .col-available {
    width: 80px;
    text-align: center;
    flex-shrink: 0;
  }

  .col-price {
    width: 120px;
    text-align: right;
    flex-shrink: 0;
  }

  .product-image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .product-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .product-select-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-top: 1px solid #e9ecef;
    background: #fafbfc;

    .selected-count {
      color: #6c757d;
      font-size: 0.9rem;
    }

    .footer-actions {
      display: flex;
      gap: 0.5rem;
    }
  }

  .gap-2 {
    gap: 0.5rem;
  }
}
</style>

